import { computed, makeObservable, observable, runInAction } from 'mobx'
import { fetchRubricPromptTestRubricResultsByUserWithDetails } from '../firestore/RoomStateRubricResult'
import { fetchRubricPromptTestRubric } from '../firestore/SlideRubric'
import { PromptRubricType } from '../models/PromptRubricType'
import { SlideRubric } from '../models/SlideRubric'
import { type RubricType } from '../types'
import { type AdminPromptVersionCubit } from './AdminPromptVersionCubit'
import { Cubit } from './core'
import { type RoomStateAnswer } from '../models/RoomStateAnswer'
import { type RoomStateEngagement } from '../models/RoomStateEngagement'
import { fetchRoomStateEngagementForRoomState } from '../firestore/RoomStateEngagement'
import { fetchRoomStateAnswers } from '../firestore/RoomStateAnswer'

export class AdminRubricPromptVersionTestCubit extends Cubit {
  promptVersionCubit: AdminPromptVersionCubit
  test: NonNullable<
    AdminPromptVersionCubit['rubricTests']['data'][number]['results']
  >[number]

  fakeRubric: SlideRubric

  @observable
  roomStateAnswersForGroup: RoomStateAnswer[] | null = null

  @observable
  roomStateAnswersPerUser: Map<string, RoomStateAnswer[]> | null = null

  @observable
  roomStateEngagement: RoomStateEngagement[] | null = null

  @observable
  results:
    | Error
    | null
    | Awaited<
        ReturnType<typeof fetchRubricPromptTestRubricResultsByUserWithDetails>
      > = null

  constructor({
    test,
    promptVersionCubit,
  }: {
    promptVersionCubit: AdminPromptVersionCubit
    test: AdminRubricPromptVersionTestCubit['test']
  }) {
    super()
    this.promptVersionCubit = promptVersionCubit
    this.test = test
    this.fakeRubric = SlideRubric.empty(this.promptVersionCubit.repository)
    makeObservable(this)
  }

  async initialize() {
    if (!(this.promptVersionCubit.prompt instanceof PromptRubricType))
      throw new Error('Prompt type is not a rubric type')

    this.fetchResults()
    this.fetchRubric()
    this.fetchRoomStateData()
  }

  async fetchRoomStateData() {
    const roomId = this.test.data.roomId

    try {
      // Fetch room state engagement
      const engagement = await fetchRoomStateEngagementForRoomState(
        this.promptVersionCubit.repository,
        { roomId }
      )
      if (this.isDisposed) return
      runInAction(() => (this.roomStateEngagement = engagement))

      // Fetch group answers (group answers use roomId as userId)
      const groupAnswers = await fetchRoomStateAnswers(
        this.promptVersionCubit.repository,
        { roomId, userId: roomId }
      )
      if (this.isDisposed) return
      runInAction(() => (this.roomStateAnswersForGroup = groupAnswers))

      // Fetch all room answers to organize per user
      // First we need to get the room state to get sectionId and assignmentId
      const roomState = this.promptVersionCubit.testRooms.get(roomId)
      if (roomState && roomState.isNotEmpty) {
        const { sectionId, assignmentId, userIds } = roomState.data
        if (sectionId && assignmentId && userIds) {
          // Fetch answers for each user
          const answersPerUser = new Map<string, RoomStateAnswer[]>()

          await Promise.all(
            userIds.map(async (userId) => {
              try {
                const userAnswers = await fetchRoomStateAnswers(
                  this.promptVersionCubit.repository,
                  { roomId, userId }
                )
                if (!this.isDisposed) {
                  answersPerUser.set(userId, userAnswers)
                }
              } catch (error) {
                // Some users might not have answers, which is fine
                // No action needed - user simply didn't answer
              }
            })
          )

          if (!this.isDisposed) {
            runInAction(() => (this.roomStateAnswersPerUser = answersPerUser))
          }
        }
      }
    } catch (error) {
      console.error('Error fetching room state data:', error)
    }
  }

  async fetchResults() {
    try {
      const results = await fetchRubricPromptTestRubricResultsByUserWithDetails(
        this.promptVersionCubit.repository,
        {
          rubricType: this.promptVersionCubit.subType as RubricType,
          promptVersionId: this.promptVersionCubit.versionId,
          testId: this.test.doc.id,
        }
      )
      if (this.isDisposed) return
      runInAction(() => (this.results = results))
    } catch (e) {
      if (this.isDisposed) return
      runInAction(() => (this.results = e as Error))
      return
    }
  }

  async fetchRubric() {
    const rubric = await fetchRubricPromptTestRubric(
      this.promptVersionCubit.repository,
      {
        rubricType: this.promptVersionCubit.subType as RubricType,
        promptVersionId: this.promptVersionCubit.versionId,
        testId: this.test.doc.id,
      }
    )
    if (this.isDisposed) return
    if (!rubric) throw new Error('No rubric found')
    this.fakeRubric.replaceModel(rubric)
  }

  @computed
  get allDataLoaded() {
    return (
      this.results !== null &&
      this.roomStateEngagement !== null &&
      this.roomStateAnswersForGroup !== null &&
      this.roomStateAnswersPerUser !== null
    )
  }
}
